import { useState, useMemo } from 'react';
import {
  Card, CardContent, Stack, TextField, MenuItem, Typography, Box, Grid, Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Chip, Alert,
} from '@mui/material';
import { Groups, TrackChanges, AccountBalanceWallet, Assignment } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { verticalsApi } from '../../api/verticals.api';
import { useVertical } from '../../hooks/useVertical';

const now = new Date().getFullYear();
const YEARS = [now - 2, now - 1, now, now + 1];

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | number }) {
  return (
    <Card sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', color: '#fff', borderRadius: 2, boxShadow: '0 6px 18px rgba(30,58,138,0.18)' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={800} lineHeight={1.15} noWrap>{value}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

/**
 * Business Head team performance: pick a vertical + year and see the head's team
 * with each member's target vs achieved revenue and owned work items, plus totals.
 * A Business Head sees their own team; an admin can use it per vertical.
 */
export function TeamPerformancePage() {
  const { verticals, activeVertical } = useVertical();
  const [verticalId, setVerticalId] = useState<number | ''>(activeVertical?.id ?? '');
  const [year, setYear] = useState<number>(now);

  const effectiveVertical = verticalId || verticals[0]?.id || '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['team-performance', effectiveVertical, year],
    queryFn: () => verticalsApi.teamPerformance({ verticalId: Number(effectiveVertical), year }),
    enabled: Boolean(effectiveVertical),
  });

  const inr = useMemo(() => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }), []);

  return (
    <>
      <PageHeader title="Team Performance" subtitle="Your team's targets, achievement and ownership for the selected business vertical." />

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Vertical" value={effectiveVertical} onChange={(e) => setVerticalId(Number(e.target.value))} sx={{ minWidth: 240 }}>
          {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ minWidth: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Stack>

      {verticals.length === 0 && <Alert severity="info">No vertical available. Pin or get access to a vertical first.</Alert>}
      {isLoading && <LinearProgress />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} md={3}><Metric icon={<Groups />} label="Team size" value={data.totals.teamSize} /></Grid>
            <Grid item xs={6} md={3}><Metric icon={<TrackChanges />} label={`Target (${data.year})`} value={`₹${inr.format(data.totals.target)}`} /></Grid>
            <Grid item xs={6} md={3}><Metric icon={<AccountBalanceWallet />} label="Achieved revenue" value={`₹${inr.format(data.totals.achievedRevenue)}`} /></Grid>
            <Grid item xs={6} md={3}><Metric icon={<Assignment />} label="Work items owned" value={data.totals.workItems} /></Grid>
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                {data.vertical.name} — Achievement {data.totals.achievementPct}%
                {data.vertical.status !== 'active' && <Chip size="small" color="default" label="OFF" sx={{ ml: 1 }} />}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Team Member</TableCell>
                    <TableCell align="right">Target</TableCell>
                    <TableCell align="right">Achieved</TableCell>
                    <TableCell align="right">Achievement</TableCell>
                    <TableCell align="right">Work Items</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.members.length === 0 && (
                    <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary" py={2} textAlign="center">No team members. Assign members under this head in Vertical Access &amp; Team.</Typography></TableCell></TableRow>
                  )}
                  {data.members.map((m) => (
                    <TableRow key={m.userId} hover>
                      <TableCell><Typography fontWeight={600}>{m.userName}</Typography><Typography variant="caption" color="text.secondary">{m.email}</Typography></TableCell>
                      <TableCell align="right">₹{inr.format(m.target)}</TableCell>
                      <TableCell align="right">₹{inr.format(m.achievedRevenue)}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" color={m.achievementPct >= 100 ? 'success' : m.achievementPct >= 60 ? 'warning' : 'default'} label={`${m.achievementPct}%`} />
                      </TableCell>
                      <TableCell align="right">{m.workItems}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>Could not load team performance.</Alert>}
    </>
  );
}
